import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { addMinutes, differenceInMinutes } from 'date-fns';
import { useIntl } from 'react-intl';

import { PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES } from 'components/bottom-service-mode/hooks/use-bottom-service-details';
import { postOrderTimeout } from 'components/bottom-service-mode/utils';
import { Fab } from 'components/fab';
import { useFeatureHomePage } from 'components/features/home-page/use-feature-home-page';
import { useIsTopServiceModeEnabled } from 'experiments/service-mode/use-is-top-servie-mode-enabled';
import { DeliveryStatus } from 'generated/rbi-graphql';
import { ISimpleHomePage } from 'generated/sanity-graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { appendObjectToQueryString } from 'hooks/navigation/utils';
import { useCart } from 'hooks/use-cart';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useReactNavigationFlag } from 'hooks/use-react-navigation-flag';
import { useUserOrders } from 'hooks/use-user-orders';
import { MODAL_TYPE } from 'pages/account/account-orders/constants';
import { DetailsModal } from 'pages/account/account-orders/details-modal';
import { useAccountOrdersFlow } from 'pages/account/account-orders/use-account-orders-flow';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';

const MAX_COMPLETED_TIME_IN_MINUTES = 10;
const MAX_IN_RESTAURANT_REDEEM_TIME_IN_MINUTES = 60;

const checkMyCodeTimeRange = (lastModDate: Date): boolean => {
  const difference = differenceInMinutes(new Date(), new Date(lastModDate));
  return difference >= 0 && difference < MAX_IN_RESTAURANT_REDEEM_TIME_IN_MINUTES;
};
const Container = Box.withConfig({
  height: 0,
});

const LayoutFabButton = ({ ...rest }) => {
  const { formatMessage } = useIntl();
  const { isAuthenticated, setOriginLocation } = useAuthContext();
  const { curbsidePickupOrderId, curbsidePickupOrderTimePlaced } = useOrderContext();
  const {
    inRestaurantRedemptionCart,
    inRestaurantRedemptionLastModificationDate,
  } = useInRestaurantRedemptionContext();
  const { linkTo } = useNavigation();
  const { pathname } = useRoute();
  const enableReactNativeNavigation = useReactNavigationFlag();
  const enableMoveSignUpAfterCart = useFlag(LaunchDarklyFlag.ENABLE_MOVE_SIGNUP_AFTER_CART);
  const [detailsModal, setDetailsModal] = useState('');
  const { loading, orders, refetch } = useUserOrders({ limit: 1 }, 'network-only');
  const { goToDetailsModal } = useAccountOrdersFlow();

  const lastModDate =
    inRestaurantRedemptionLastModificationDate ||
    addMinutes(new Date(), -MAX_IN_RESTAURANT_REDEEM_TIME_IN_MINUTES);

  const [shouldShowMyCode, setShouldShowMyCode] = useState(checkMyCodeTimeRange(lastModDate));
  const handleDisplayDetailsModal = useCallback(
    (orderId: any) => {
      if (enableReactNativeNavigation) {
        return goToDetailsModal(orderId, MODAL_TYPE.SHOW_DETAILS);
      }
      setDetailsModal(orderId);
    },
    [enableReactNativeNavigation, goToDetailsModal]
  );

  const handleOnModalDismiss = useCallback(() => setDetailsModal(''), []);
  const { logRBIEvent } = useMParticleContext();

  // @TODO: Currently we have duplicated logic for recentOrders between FAB Button, Top service Mode and Service Notification
  // We must find a way to avoid this.
  const isCurrentDeliveryOrder = useMemo(() => {
    const deliveryOrder = orders?.[0];
    const orderStatus = deliveryOrder?.delivery?.status;
    const failedOrderStatus = [
      DeliveryStatus.ORDER_CANCELLED,
      DeliveryStatus.ORDER_ABANDONED,
      DeliveryStatus.ORDER_DROPPED_OFF,
    ];

    if (!deliveryOrder || !orderStatus || failedOrderStatus.includes(orderStatus)) {
      return null;
    }
    return deliveryOrder;
  }, [orders]);

  const handleViewOrder = useCallback(() => {
    if (!loading && isCurrentDeliveryOrder) {
      return linkTo(`${routes.orderConfirmation}/${isCurrentDeliveryOrder.rbiOrderId}`);
    }
    if (!loading && orders[0]) {
      handleDisplayDetailsModal(orders[0].rbiOrderId);
    }
  }, [handleDisplayDetailsModal, isCurrentDeliveryOrder, linkTo, loading, orders]);
  const isRecentOrder = useCallback((currentOrderTime: string): boolean => {
    const difference = differenceInMinutes(new Date(), new Date(currentOrderTime));
    return difference >= 0 && difference < MAX_COMPLETED_TIME_IN_MINUTES;
  }, []);

  const handleStartOrder = () => {
    return linkTo(routes.menu);
  };

  /* We need to add this bc we need to know instantly if we need to show the viewOrder button*/
  useEffect(() => {
    if (pathname === routes.base) {
      refetch();
    }
  }, [pathname, refetch]);

  const inRestaurantRedemptionCartTimeOutCheckHandler = useCallback(() => {
    setShouldShowMyCode(checkMyCodeTimeRange(lastModDate));
  }, [lastModDate]);

  useEffect(() => {
    const interval = setInterval(inRestaurantRedemptionCartTimeOutCheckHandler, 2000);
    return () => clearInterval(interval);
  }, [inRestaurantRedemptionCartTimeOutCheckHandler]);

  const shouldShowViewOrderButton = isRecentOrder((orders[0] as any)?.updatedAt);
  const logCartClick = () => {
    logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Other,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        text: 'View Cart',
      },
    });
  };

  const setCartRedirection = () => {
    setOriginLocation(routes.cart);
  };

  const { numberOfItemsInCart } = useCart();

  let to;
  let onPress;
  let fabCaption;
  let badgeCount;

  const isPreConfirmCurbside = () =>
    curbsidePickupOrderId &&
    postOrderTimeout(
      curbsidePickupOrderTimePlaced,
      PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES
    );

  if (inRestaurantRedemptionCart?.length && shouldShowMyCode) {
    fabCaption = formatMessage({ id: 'myCode' });
    to = routes.redeem;
    badgeCount =
      inRestaurantRedemptionCart.reduce((acc, currentValue) => acc + currentValue.quantity, 0) ||
      undefined;
  } else if (isPreConfirmCurbside() && isAuthenticated) {
    fabCaption = formatMessage({ id: 'iHaveArrived' });
    onPress = () => {
      linkTo(
        appendObjectToQueryString(`${routes.orderConfirmation}/${curbsidePickupOrderId}`, {
          showToast: true,
        })
      );
    };
  } else if (numberOfItemsInCart < 1 && !shouldShowViewOrderButton) {
    fabCaption = formatMessage({ id: 'startOrder' });
    onPress = handleStartOrder;
    if (pathname !== routes.base) {
      return null;
    }
  } else if (numberOfItemsInCart > 0) {
    fabCaption = formatMessage({ id: 'viewCart' });
    if (isAuthenticated || enableMoveSignUpAfterCart) {
      to = routes.cart;
      onPress = logCartClick;
    } else {
      to = routes.signUp;
      onPress = setCartRedirection;
    }
    badgeCount = numberOfItemsInCart;
    if (pathname !== routes.base && !pathname.includes(routes.menu)) {
      return null;
    }
  } else if (isAuthenticated && shouldShowViewOrderButton) {
    fabCaption = formatMessage({ id: 'viewOrder' });
    onPress = handleViewOrder;
    if (pathname !== routes.base) {
      return null;
    }
  }

  return (
    <Container>
      <Fab
        caption={fabCaption}
        badge={badgeCount}
        to={to}
        testID={`nav-${fabCaption}`}
        onPress={onPress}
        {...rest}
      />
      {detailsModal && (
        <DetailsModal
          rbiOrderId={detailsModal}
          onModalDismiss={handleOnModalDismiss}
          type={MODAL_TYPE.SHOW_DETAILS}
          setDetailsModal={handleDisplayDetailsModal}
        />
      )}
    </Container>
  );
};

export default function LayoutFabButtonContainer() {
  const { featureHomePage, featureHomePageLoading } = useFeatureHomePage();

  const { pathname } = useRoute();
  const isDesktop = useIsDesktopBreakpoint();
  const enableMainFabButton = useFlag(LaunchDarklyFlag.ENABLE_MAIN_FAB_BUTTON);
  const enableTopServiceMode = useIsTopServiceModeEnabled();
  const isComboOrPickerPage = pathname.includes('picker') || pathname.includes('combo');
  const isMenuPage = pathname.includes(routes.menu);
  const isItemMenu = pathname.search(/\item\b/) >= 0;
  const isBaseRoute = pathname === routes.base;

  const homePageHasSimpleHomePage = useMemo(
    () =>
      featureHomePage?.components?.some(
        (cmp): cmp is ISimpleHomePage => cmp?.__typename === 'SimpleHomePage'
      ) ?? false,
    [featureHomePage?.components]
  );

  const shouldHideFabButton = () => {
    if (isDesktop || !enableMainFabButton || !enableTopServiceMode) {
      return true;
    }

    if (!isMenuPage && !isBaseRoute) {
      return true;
    }

    if (isMenuPage && (isComboOrPickerPage || isItemMenu)) {
      return true;
    }

    if (isBaseRoute && (featureHomePageLoading || homePageHasSimpleHomePage)) {
      return true;
    }

    return false;
  };

  return shouldHideFabButton() ? null : <LayoutFabButton />;
}
